import type { NavItem, NavSection } from "@/constants/appSidebar";
import { QUICK_MENU_HOME, QUICK_MENU_LABELS } from "@/constants/quickMenu";
import type { QuickMenuChoiceGroup } from "@/types/quickMenu";

/**
 * 띠에 올릴 수 있는 메뉴 — 이 사용자의 메뉴 묶음에서 첫 칸(`/home`)을 뺀 것.
 *
 * 첫 칸은 늘 맨 앞에 있어 고르는 대상이 아니다. 제목은 `QUICK_MENU_LABELS` 로 덮어쓰고,
 * 이 때문에 비게 된 묶음은 버린다.
 */
export const selectableSections = (sections: NavSection[]): NavSection[] =>
  sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => item.url !== QUICK_MENU_HOME.url)
        .map((item) => ({
          ...item,
          title: QUICK_MENU_LABELS[item.url] ?? item.title,
        })),
    }))
    .filter((section) => section.items.length > 0);

/** 고른 url 에 해당하는 칸. 순서는 고른 순서가 아니라 메뉴 정의의 순서다. */
export const pickQuickMenu = (
  sections: NavSection[],
  selectedUrls: string[],
): NavItem[] => {
  const selected = new Set(selectedUrls);
  return selectableSections(sections)
    .flatMap((section) => section.items)
    .filter((item) => selected.has(item.url));
};

/** 설정 다이얼로그가 그리는 묶음별 체크박스 줄. `draftUrls` 에 있는 줄이 체크된다. */
export const groupQuickMenuChoices = (
  sections: NavSection[],
  draftUrls: string[],
): QuickMenuChoiceGroup[] => {
  const draft = new Set(draftUrls);
  return selectableSections(sections).map((section) => ({
    id: section.id,
    label: section.label,
    choices: section.items.map((item) => ({
      title: item.title,
      url: item.url,
      icon: item.icon,
      selected: draft.has(item.url),
    })),
  }));
};

/** 두 url 목록이 순서·중복과 무관하게 같은 집합인지. */
export const isSameUrlSet = (a: string[], b: string[]): boolean => {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((url) => right.has(url));
};
